// src/stores/PreLicenseStore.ts

import { makeAutoObservable, runInAction } from 'mobx';
import {
  PreLicenseRequest,
  ExternalWork,
  CMOOrganization,
  ExternalWorkSearchRequest,
  ExternalWorkSearchResponse,
  PreLicenseStats
} from '@/types/pre-license';
import cmoService from '@/services/cmoService';
import type { RootStore } from './index';

export class PreLicenseStore {
  rootStore: RootStore;

  // Pre-License Requests
  userRequests: PreLicenseRequest[] = [];
  legalTeamRequests: PreLicenseRequest[] = [];
  selectedRequest: PreLicenseRequest | null = null;

  // External Works
  searchResults: ExternalWorkSearchResponse | null = null;
  selectedWork: ExternalWork | null = null;
  recentSearches: string[] = [];

  // CMO Organizations
  availableCMOs: CMOOrganization[] = [];

  // Statistics
  dashboardStats: PreLicenseStats | null = null;

  // UI State
  isLoading = false;
  isSubmitting = false;
  isSearching = false;
  error: string | null = null;

  // Filters
  userRequestFilters = {
    status: 'all',
    priority: 'all',
    dateRange: '30d'
  };

  legalTeamFilters = {
    status: 'pending_review',
    priority: 'all',
    assignedOfficer: 'all',
    requestType: 'all',
    dateRange: '30d'
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // External Work Search
  async searchExternalWorks(request: ExternalWorkSearchRequest) {
    this.setSearching(true);
    this.setError(null);

    try {
      const results = await cmoService.searchExternalWorks(request);

      runInAction(() => {
        this.searchResults = results;

        // Save to recent searches
        if (request.query && !this.recentSearches.includes(request.query)) {
          this.recentSearches = [request.query, ...this.recentSearches.slice(0, 9)];
        }
      });

      return results;
    } catch (error) {
      this.setError('Có lỗi xảy ra khi tìm kiếm tác phẩm');
      console.error('External work search error:', error);
      return null;
    } finally {
      this.setSearching(false);
    }
  }

  async loadCMOs() {
    try {
      const cmos = await cmoService.getAllCMOs();
      runInAction(() => {
        this.availableCMOs = cmos;
      });
    } catch (error) {
      console.error('Load CMOs error:', error);
    }
  }

  selectExternalWork(work: ExternalWork) {
    this.selectedWork = work;
  }

  clearSearchResults() {
    this.searchResults = null;
    this.selectedWork = null;
  }

  // Pre-License Request Management
  async createPreLicenseRequest(requestData: Partial<PreLicenseRequest>) {
    this.setSubmitting(true);
    this.setError(null);

    try {
      const request = await cmoService.createPreLicenseRequest(requestData);

      runInAction(() => {
        this.userRequests = [request, ...this.userRequests];
      });

      return request;
    } catch (error) {
      this.setError('Có lỗi xảy ra khi tạo yêu cầu');
      console.error('Create pre-license request error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  async loadUserRequests(userId: string, refresh = false) {
    if (!refresh && this.userRequests.length > 0) return;

    this.setLoading(true);
    this.setError(null);

    try {
      const filters = this.buildUserRequestFilters();
      const requests = await cmoService.getUserPreLicenseRequests(userId, filters);

      runInAction(() => {
        this.userRequests = requests;
      });
    } catch (error) {
      this.setError('Có lỗi xảy ra khi tải danh sách yêu cầu');
      console.error('Load user requests error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  async loadLegalTeamRequests(refresh = false) {
    if (!refresh && this.legalTeamRequests.length > 0) return;

    this.setLoading(true);
    this.setError(null);

    try {
      const filters = this.buildLegalTeamFilters();
      const requests = await cmoService.getLegalTeamRequests(filters);

      runInAction(() => {
        this.legalTeamRequests = requests;
      });
    } catch (error) {
      this.setError('Có lỗi xảy ra khi tải danh sách yêu cầu');
      console.error('Load legal team requests error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  async updatePreLicenseRequest(documentId: string, updateData: Partial<PreLicenseRequest>) {
    this.setSubmitting(true);
    this.setError(null);

    try {
      const updatedRequest = await cmoService.updatePreLicenseRequest(documentId, updateData);

      runInAction(() => {
        // Update in user requests
        const userIndex = this.userRequests.findIndex(r => r.documentId === documentId);
        if (userIndex !== -1) {
          this.userRequests[userIndex] = updatedRequest;
        }

        // Update in legal team requests
        const legalIndex = this.legalTeamRequests.findIndex(r => r.documentId === documentId);
        if (legalIndex !== -1) {
          this.legalTeamRequests[legalIndex] = updatedRequest;
        }

        // Update selected request
        if (this.selectedRequest?.documentId === documentId) {
          this.selectedRequest = updatedRequest;
        }
      });

      return updatedRequest;
    } catch (error) {
      this.setError('Có lỗi xảy ra khi cập nhật yêu cầu');
      console.error('Update pre-license request error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  async cancelPreLicenseRequest(documentId: string) {
    return this.updatePreLicenseRequest(documentId, {
      status: 'cancelled',
      
    });
  }

  async resendPreLicenseRequest(documentId: string) {
    return this.updatePreLicenseRequest(documentId, {
      status: 'submitted',
      requestDate: new Date().toISOString(),
      
    });
  }

  async assignLegalOfficer(documentId: string, officerName: string) {
    return this.updatePreLicenseRequest(documentId, {
      'vietrroProcessing.assignedLegalOfficer': officerName,
      'vietrroProcessing.legalReviewStartDate': new Date().toISOString(),
      status: 'legal_review',
      
    });
  }

  async addCommunication(documentId: string, communication: any) {
    const request = this.getRequestById(documentId);
    if (!request) return;

    const updatedCommunications = [
      ...(request.communications || []),
      {
        ...communication,
        id: Date.now().toString(),
        date: new Date().toISOString()
      }
    ];

    return this.updatePreLicenseRequest(documentId, {
      communications: updatedCommunications
    });
  }

  // Statistics
  async loadDashboardStats() {
    try {
      // Mock implementation - replace with actual API call
      const mockStats: PreLicenseStats = {
        total: 125,
        byStatus: {
          submitted: 23,
          legal_review: 18,
          cmo_contacted: 12,
          cmo_negotiating: 8,
          approved: 45,
          rejected: 19
        },
        byType: {
          cmo_work: 78,
          external_work: 32,
          internal_work: 15
        },
        byPriority: {
          urgent: 5,
          high: 18,
          medium: 67,
          low: 35
        },
        avgProcessingTime: 14.5,
        successRate: 78.2,
        totalRevenue: 2450000,
        topCMOs: [
          { name: 'ASCAP', count: 25, successRate: 85.2 },
          { name: 'BMI', count: 18, successRate: 72.1 },
          { name: 'GEMA', count: 15, successRate: 90.3 }
        ],
        monthlyTrends: []
      };

      runInAction(() => {
        this.dashboardStats = mockStats;
      });
    } catch (error) {
      console.error('Load dashboard stats error:', error);
    }
  }

  // Filter Management
  updateUserRequestFilters(filters: Partial<typeof this.userRequestFilters>) {
    this.userRequestFilters = { ...this.userRequestFilters, ...filters };
  }

  updateLegalTeamFilters(filters: Partial<typeof this.legalTeamFilters>) {
    this.legalTeamFilters = { ...this.legalTeamFilters, ...filters };
  }

  private buildUserRequestFilters() {
    const filters: any = {};

    if (this.userRequestFilters.status !== 'all') {
      filters.status = { $eq: this.userRequestFilters.status };
    }

    if (this.userRequestFilters.priority !== 'all') {
      filters.priority = { $eq: this.userRequestFilters.priority };
    }

    if (this.userRequestFilters.dateRange !== 'all') {
      const now = new Date();
      const daysMap = { '7d': 7, '30d': 30, '90d': 90 };
      const days = daysMap[this.userRequestFilters.dateRange];
      if (days) {
        const cutoffDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
        filters.requestDate = { $gte: cutoffDate.toISOString() };
      }
    }

    return filters;
  }

  private buildLegalTeamFilters() {
    const filters: any = {};

    if (this.legalTeamFilters.status !== 'all') {
      if (this.legalTeamFilters.status === 'pending_review') {
        filters.status = { $in: ['submitted', 'legal_review'] };
      } else {
        filters.status = { $eq: this.legalTeamFilters.status };
      }
    }

    if (this.legalTeamFilters.priority !== 'all') {
      filters.priority = { $eq: this.legalTeamFilters.priority };
    }

    if (this.legalTeamFilters.requestType !== 'all') {
      filters.requestType = { $eq: this.legalTeamFilters.requestType };
    }

    if (this.legalTeamFilters.assignedOfficer !== 'all') {
      filters['vietrroProcessing.assignedLegalOfficer'] = { $eq: this.legalTeamFilters.assignedOfficer };
    }

    if (this.legalTeamFilters.dateRange !== 'all') {
      const now = new Date();
      const daysMap = { '7d': 7, '30d': 30, '90d': 90 };
      const days = daysMap[this.legalTeamFilters.dateRange];
      if (days) {
        const cutoffDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
        filters.requestDate = { $gte: cutoffDate.toISOString() };
      }
    }

    return filters;
  }

  // Getters
  get filteredUserRequests() {
    return this.userRequests.filter(request => {
      const filters = this.userRequestFilters;

      if (filters.status !== 'all' && request.status !== filters.status) {
        return false;
      }

      if (filters.priority !== 'all' && request.priority !== filters.priority) {
        return false;
      }

      if (filters.dateRange !== 'all') {
        const now = new Date();
        const daysMap = { '7d': 7, '30d': 30, '90d': 90 };
        const days = daysMap[filters.dateRange];
        if (days) {
          const cutoffDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
          if (new Date(request.requestDate) < cutoffDate) {
            return false;
          }
        }
      }

      return true;
    });
  }

  get filteredLegalTeamRequests() {
    return this.legalTeamRequests.filter(request => {
      const filters = this.legalTeamFilters;

      if (filters.status !== 'all') {
        if (filters.status === 'pending_review') {
          if (!['submitted', 'legal_review'].includes(request.status)) {
            return false;
          }
        } else if (request.status !== filters.status) {
          return false;
        }
      }

      if (filters.priority !== 'all' && request.priority !== filters.priority) {
        return false;
      }

      if (filters.requestType !== 'all' && request.requestType !== filters.requestType) {
        return false;
      }

      if (filters.assignedOfficer !== 'all') {
        const assignedOfficer = request.vietrroProcessing?.assignedLegalOfficer;
        if (assignedOfficer !== filters.assignedOfficer) {
          return false;
        }
      }

      return true;
    });
  }

  get userRequestsByStatus() {
    const statusCounts = {
      all: this.userRequests.length,
      pending: 0,
      approved: 0,
      rejected: 0,
      negotiating: 0
    };

    this.userRequests.forEach(request => {
      switch (request.status) {
        case 'submitted':
        case 'legal_review':
        case 'cmo_contacted':
          statusCounts.pending++;
          break;
        case 'cmo_negotiating':
          statusCounts.negotiating++;
          break;
        case 'approved':
        case 'cmo_approved':
          statusCounts.approved++;
          break;
        case 'rejected':
          statusCounts.rejected++;
          break;
      }
    });

    return statusCounts;
  }

  get legalTeamRequestsByStatus() {
    const statusCounts = {
      all: this.legalTeamRequests.length,
      pending: 0,
      inProgress: 0,
      completed: 0
    };

    this.legalTeamRequests.forEach(request => {
      switch (request.status) {
        case 'submitted':
          statusCounts.pending++;
          break;
        case 'legal_review':
        case 'cmo_contacted':
        case 'cmo_negotiating':
          statusCounts.inProgress++;
          break;
        case 'approved':
        case 'rejected':
        case 'cmo_approved':
          statusCounts.completed++;
          break;
      }
    });

    return statusCounts;
  }

  // Utility Methods
  getRequestById(documentId: string): PreLicenseRequest | null {
    return this.userRequests.find(r => r.documentId === documentId) ||
      this.legalTeamRequests.find(r => r.documentId === documentId) ||
      null;
  }

  selectRequest(request: PreLicenseRequest) {
    this.selectedRequest = request;
  }

  clearSelectedRequest() {
    this.selectedRequest = null;
  }

  // State Setters
  private setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  private setSubmitting(submitting: boolean) {
    this.isSubmitting = submitting;
  }

  private setSearching(searching: boolean) {
    this.isSearching = searching;
  }

  private setError(error: string | null) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  // Initialize
  async initialize() {
    try {
      await this.loadCMOs();
      await this.loadDashboardStats();
    } catch (error) {
      console.error('PreLicenseStore initialization error:', error);
    }
  }

  // Reset
  reset() {
    this.userRequests = [];
    this.legalTeamRequests = [];
    this.selectedRequest = null;
    this.searchResults = null;
    this.selectedWork = null;
    this.dashboardStats = null;
    this.isLoading = false;
    this.isSubmitting = false;
    this.isSearching = false;
    this.error = null;
    this.userRequestFilters = {
      status: 'all',
      priority: 'all',
      dateRange: '30d'
    };
    this.legalTeamFilters = {
      status: 'pending_review',
      priority: 'all',
      assignedOfficer: 'all',
      requestType: 'all',
      dateRange: '30d'
    };
  }
}

export default PreLicenseStore;
